import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const RELATIVE = {
    today: (now) => now.startOf("day"),
    tonight: (now) => now.startOf("day"),
    tomorrow: (now) => now.add(1, "day").startOf("day"),
    "day after tomorrow": (now) => now.add(2, "day").startOf("day"),
    "end of week": (now) => endOfWorkWeek(now),
    eow: (now) => endOfWorkWeek(now),
    "end of month": (now) => endOfMonth(now),
    eom: (now) => endOfMonth(now),
    "end of quarter": (now) => endOfQuarter(now),
    eoq: (now) => endOfQuarter(now),
    "next week": (now) => now.add(1, "week").startOf("week").add(1, "day"), // Monday
    "next monday": (now) => nextWeekday(now, 1),
    "next tuesday": (now) => nextWeekday(now, 2),
    "next wednesday": (now) => nextWeekday(now, 3),
    "next thursday": (now) => nextWeekday(now, 4),
    "next friday": (now) => nextWeekday(now, 5),
    "next saturday": (now) => nextWeekday(now, 6),
    "next sunday": (now) => nextWeekday(now, 0),
    monday: (now) => upcomingWeekday(now, 1),
    tuesday: (now) => upcomingWeekday(now, 2),
    wednesday: (now) => upcomingWeekday(now, 3),
    thursday: (now) => upcomingWeekday(now, 4),
    friday: (now) => upcomingWeekday(now, 5),
    saturday: (now) => upcomingWeekday(now, 6),
    sunday: (now) => upcomingWeekday(now, 0)
};

function endOfWorkWeek(now) {
    let eow = now.day(5); // Friday
    if (now.day() > 5) eow = now.add(1, "week").day(5);
    return eow.startOf("day");
}

function endOfMonth(now) {
    let eom = now.endOf("month").startOf("day");
    if (eom.day() === 0) eom = eom.subtract(2, "day");
    if (eom.day() === 6) eom = eom.subtract(1, "day");
    return eom;
}

function endOfQuarter(now) {
    const q = Math.floor(now.month() / 3);
    return now.month(q * 3 + 2).endOf("month").startOf("day");
}

function nextWeekday(now, target) {
    // Strict "next": always at least 7 days out. "Next Friday" on a Thursday
    // means 8 days from now, not tomorrow.
    const today = now.day();
    let diff = target - today;
    while (diff < 7) diff += 7;
    return now.add(diff, "day").startOf("day");
}

function upcomingWeekday(now, target) {
    // Loose "this monday" / "monday" — the next occurrence, today included
    // only if it matches and the time hasn't passed yet (we don't model time).
    const today = now.day();
    let diff = target - today;
    if (diff <= 0) diff += 7;
    return now.add(diff, "day").startOf("day");
}

const ABSOLUTE_FORMATS = [
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "MMMM D YYYY",
    "MMM D YYYY",
    "D MMMM YYYY",
    "D MMM YYYY",
    "MMMM D",
    "MMM D",
    "D MMMM",
    "D MMM"
];

/**
 * Parse a natural-language date phrase into an ISO date string.
 *
 * Rules:
 *  - Relative phrases ("tomorrow", "next monday", "EOW") resolve from `now`.
 *  - Absolute partials with no year ("7 February", "Feb 7") default to the
 *    current year, but if that lands in the past we bump to next year.
 *  - "in N days/weeks/months" works.
 *  - Returns ISO date string (YYYY-MM-DD) or `null` if unparseable.
 */
export function resolveDate(phrase, now = dayjs()) {
    if (phrase == null) return null;
    const raw = String(phrase).trim().toLowerCase();
    if (!raw) return null;

    if (RELATIVE[raw]) return RELATIVE[raw](now).format("YYYY-MM-DD");

    // "in N days/weeks/months"
    const inMatch = raw.match(/^in\s+(\d+)\s+(day|days|week|weeks|month|months)$/);
    if (inMatch) {
        const n = parseInt(inMatch[1], 10);
        const unit = inMatch[2].replace(/s$/, "");
        return now.add(n, unit).startOf("day").format("YYYY-MM-DD");
    }

    // Try absolute formats. dayjs in strict mode rejects malformed input.
    for (const fmt of ABSOLUTE_FORMATS) {
        const parsed = dayjs(phrase, fmt, true);
        if (parsed.isValid()) {
            // Year-less formats default to 1900 (varies by version) — bump.
            let result = parsed;
            if (!fmt.includes("YYYY")) {
                result = parsed.year(now.year());
                if (result.isBefore(now.startOf("day"))) {
                    result = result.add(1, "year");
                }
            }
            return result.startOf("day").format("YYYY-MM-DD");
        }
    }

    // Last resort: dayjs's permissive parser. If it produces a date in the
    // past, we still bump it forward by a year — the same "smart default"
    // the user described for "7 February".
    const loose = dayjs(phrase);
    if (loose.isValid()) {
        let result = loose;
        if (result.isBefore(now.startOf("day"))) {
            result = result.add(1, "year");
        }
        return result.startOf("day").format("YYYY-MM-DD");
    }

    return null;
}

export function formatHumanDate(iso) {
    if (!iso) return null;
    const d = dayjs(iso);
    if (!d.isValid()) return null;
    return d.format("ddd D MMM YYYY");
}
